import { GenericEsignDocument, SigningService } from '@fastre/core/schemas/esign'
import { formatAddress } from '@fastre/core/schemas/generic'
import {
    AddRounded,
    Cancel,
    Check,
    DeleteRounded,
    MoreVertRounded,
    MoveToInbox,
    Visibility,
} from '@mui/icons-material'
import {
    Box,
    Button,
    Dropdown,
    IconButton,
    ListItemDecorator,
    Menu,
    MenuButton,
    MenuItem,
    Stack,
    Step,
    StepIndicator,
    Stepper,
    Typography,
} from '@mui/joy'
import { useApi } from 'api'
import PdfModal from 'components/pdfModal'
import { useShowSnack } from 'components/snackbar'
import { useOrgId } from 'helpers'
import { prop } from 'ramda'
import { useState } from 'react'
import { useListingContext } from '../listingProvider'
import AbandonFormModal from '../sharedModals/abandonFormModal'
import AddAttachmentModal from '../sharedModals/addAttachmentsModal'
import IframeModal from '../sharedModals/iframeModal'
import CreateDocumentModal from './createDocumentModal'

enum FormStage {
    'voided' = -2,
    'declined' = -1,
    'none' = 0,
    'created',
    'sent',
    'completed',
    'manual',
}

const getFormStage = (document: GenericEsignDocument | undefined): FormStage => {
    if (!document) {
        return FormStage.none
    }

    switch (document.form.status) {
        case 'draft':
            return FormStage.created
        case 'sent':
        case 'delivered':
            return FormStage.sent
        case 'completed':
            return FormStage.completed
        case 'declined':
            return FormStage.declined
        case 'voided':
            return FormStage.voided
        default:
            return FormStage.none
    }
}

interface Props {
    title: string
}

const GenericDocumentStepper = ({ title }: Props) => {
    const signingService: SigningService = 'annature'
    const { listing, refreshListing, setListing } = useListingContext()
    const showSnack = useShowSnack()
    const orgId = useOrgId()
    const api = useApi()

    const [viewLoading, setViewLoading] = useState(false)
    const [loading, setLoading] = useState(false)
    const [previewPdf, setPreviewPdf] = useState<{
        url: string
        useAuth?: boolean
        isRawPdf?: boolean
        title: string
    }>()
    const [editUrl, setEditUrl] = useState<{ url: string; documentId: string }>()
    const [showUploadForm, setShowUploadForm] = useState(false)
    const [showAbandonFormDialog, setShowAbandonFormDialog] = useState<string | undefined>()
    const [showAddAttachmentsModal, setShowAddAttachmentsModal] = useState<string | undefined>()
    const [showCreateDocumentModal, setShowCreateDocumentModal] = useState(false)

    const documents = Object.values(listing.genericEsignDocuments ?? {})

    const ViewFormButton = ({ document }: { document: GenericEsignDocument }) => (
        <Button
            variant="outlined"
            loading={viewLoading}
            onClick={async () => {
                const formStage = getFormStage(document)

                switch (formStage) {
                    case FormStage.completed:
                    case FormStage.manual:
                        setViewLoading(true)
                        const url = await api
                            .post(
                                `${import.meta.env.VITE_APP_API_URL}/${orgId}/listing/sale/${listing.listingId}/annature/viewcompletedform`,
                                {
                                    formType: 'generic',
                                    formId: document.form.id,
                                },
                            )
                            .then(prop('data'))
                        setPreviewPdf({
                            title: document.formName,
                            url,
                            useAuth: false,
                            //isRawPdf: true,
                        })
                        setViewLoading(false)
                        break
                    case FormStage.sent:
                        setViewLoading(true)
                        const sentUrl = await api
                            .post(
                                `${import.meta.env.VITE_APP_API_URL}/${orgId}/listing/sale/${listing.listingId}/annature/viewsent`,
                                {
                                    formType: 'generic',
                                    formId: document.form.id,
                                },
                            )
                            .then(prop('data'))
                        setPreviewPdf({
                            title: document.formName,
                            url: sentUrl,
                            useAuth: false,
                            isRawPdf: true,
                        })
                        setViewLoading(false)
                        break
                    case FormStage.created:
                        setViewLoading(true)
                        const viewData = await api
                            .post(`/listing/sale/${listing.listingId}/annature/edit`, {
                                formType: 'generic',
                                formId: document.form.id,
                            })
                            .then(prop('data'))

                        setEditUrl({ url: viewData.url, documentId: document.form.id })
                        setViewLoading(false)
                        break
                    default:
                        if (document && document.form && 'id' in document.form) {
                            setPreviewPdf({
                                title: document.formName,
                                url: `${import.meta.env.VITE_APP_API_URL}/${orgId}/${signingService}/${document.form.id}/view`,
                                useAuth: true,
                            })
                        }
                        break
                }
            }}
            startDecorator={<Visibility />}
        >
            View Document
        </Button>
    )

    // HeaderSection Component
    const HeaderSection = () => (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                maxWidth: '600px',
            }}
        >
            <Typography level="h4">{title}</Typography>
            <Dropdown>
                <MenuButton
                    slots={{ root: IconButton }}
                    slotProps={{ root: { size: 'sm' } }}
                >
                    <MoreVertRounded fontSize="small" />
                </MenuButton>
                <Menu>
                    <MenuItem onClick={() => setShowCreateDocumentModal(true)}>
                        <ListItemDecorator>
                            <AddRounded />
                        </ListItemDecorator>
                        Create New Document
                    </MenuItem>
                </Menu>
            </Dropdown>
            <Box sx={{ flexGrow: 1 }} />
            <Button
                loading={loading}
                onClick={() => setShowCreateDocumentModal(true)}
                startDecorator={<AddRounded />}
            >
                Create Document
            </Button>
        </Box>
    )

    // DocumentList Component
    const DocumentList = () => {
        if (documents.length === 0) {
            return (
                <Typography
                    //level="body-lg"
                    sx={{ mt: 2 }}
                >
                    No documents created yet. Create your first document to get started.
                </Typography>
            )
        }

        return (
            <Stack
                spacing={2}
                sx={{ mt: 2, maxWidth: '600px' }}
            >
                {documents.map(document => {
                    const formStage = getFormStage(document)

                    return (
                        <Box
                            key={document.form.id}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 'sm',
                                p: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography level="title-lg">{document.formName}</Typography>
                                </Box>
                                <Dropdown>
                                    <MenuButton
                                        slots={{ root: IconButton }}
                                        slotProps={{ root: { size: 'sm' } }}
                                    >
                                        <MoreVertRounded fontSize="small" />
                                    </MenuButton>
                                    <Menu>
                                        <MenuItem onClick={() => setShowAbandonFormDialog(document.form.id)}>
                                            <ListItemDecorator>
                                                <DeleteRounded />
                                            </ListItemDecorator>
                                            Delete Document
                                        </MenuItem>
                                    </Menu>
                                </Dropdown>
                                <ViewFormButton document={document} />
                            </Box>

                            <Stepper
                                orientation="vertical"
                                sx={{ width: '100%' }}
                            >
                                <Step
                                    indicator={
                                        <StepIndicator
                                            variant={formStage >= FormStage.created ? 'solid' : 'soft'}
                                            color="primary"
                                        >
                                            {formStage >= FormStage.created ? <Check /> : undefined}
                                        </StepIndicator>
                                    }
                                    sx={{
                                        '&::after': {
                                            ...(formStage >= FormStage.created && {
                                                bgcolor: 'primary.solidBg',
                                            }),
                                        },
                                    }}
                                >
                                    <Typography>Create Document</Typography>
                                </Step>
                                <Step
                                    indicator={
                                        <StepIndicator
                                            variant={FormStage.sent <= formStage ? 'solid' : 'soft'}
                                            color={FormStage.sent <= formStage ? 'primary' : 'neutral'}
                                        >
                                            {formStage >= FormStage.sent ? <Check /> : undefined}
                                        </StepIndicator>
                                    }
                                    sx={{
                                        '&::after': {
                                            ...(formStage >= FormStage.sent && {
                                                bgcolor: 'primary.solidBg',
                                            }),
                                        },
                                    }}
                                >
                                    <Typography>Send</Typography>
                                </Step>

                                {document.form.recipients?.map(recipient => (
                                    <Step
                                        key={recipient.id}
                                        indicator={
                                            <StepIndicator
                                                variant={
                                                    recipient.status === 'completed' ||
                                                    recipient.status === 'signed'
                                                        ? 'solid'
                                                        : 'soft'
                                                }
                                                color={
                                                    recipient.status === 'completed' ||
                                                    recipient.status === 'signed'
                                                        ? 'primary'
                                                        : recipient.status === 'declined'
                                                          ? 'danger'
                                                          : 'neutral'
                                                }
                                            >
                                                {recipient.status === 'completed' ||
                                                recipient.status === 'signed' ? (
                                                    <Check />
                                                ) : recipient.status === 'declined' ? (
                                                    <Cancel />
                                                ) : recipient.status === 'delivered' ? (
                                                    <MoveToInbox />
                                                ) : undefined}
                                            </StepIndicator>
                                        }
                                        sx={{
                                            '&::after': {
                                                ...((recipient.status === 'completed' ||
                                                    recipient.status === 'signed') && {
                                                    bgcolor: 'primary.solidBg',
                                                }),
                                            },
                                        }}
                                    >
                                        <Stack>
                                            <Typography>{recipient.name} Signature</Typography>
                                            {recipient.declined_reason && (
                                                <Typography level="body-xs">
                                                    reason: {recipient.declined_reason}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>
                    )
                })}
            </Stack>
        )
    }

    return (
        <>
            <HeaderSection />
            <DocumentList />
            {previewPdf?.url && (
                <PdfModal
                    title={previewPdf.title}
                    pdf={previewPdf.url}
                    onClose={() => setPreviewPdf(undefined)}
                    fileName={`${formatAddress(listing.listingAddress) || 'listing'}_generic_document.pdf`}
                    withAuth={previewPdf.useAuth}
                    isRawPdf={previewPdf.isRawPdf}
                />
            )}
            <IframeModal
                title="Edit Document"
                url={editUrl?.url}
                documentId={editUrl?.documentId}
                onClose={() => setEditUrl(undefined)}
                signingService={signingService}
                formType="generic"
                listingId={listing.listingId}
            />
            {/*<UploadFormModal
                open={showUploadForm}
                onClose={url => {
                    setShowUploadForm(false)
                    if (url) {
                        setEditUrl({ url, documentId: '' })
                    }
                }}
                formType="generic"
                signingService={signingService}
            />*/}
            <AbandonFormModal
                open={showAbandonFormDialog != undefined}
                onClose={() => setShowAbandonFormDialog(undefined)}
                formType="generic"
                documentId={showAbandonFormDialog}
            />
            <AddAttachmentModal
                open={showAddAttachmentsModal != undefined}
                onClose={data => {
                    if (data) {
                        setEditUrl({ url: data.editUrl, documentId: data.envelopeId })
                    }
                    setShowAddAttachmentsModal(undefined)
                }}
                formType="generic"
            />
            <CreateDocumentModal
                open={showCreateDocumentModal}
                onClose={data => {
                    if (data) {
                        setEditUrl({ url: data.editUrl, documentId: data.documentId })
                    }
                    setShowCreateDocumentModal(false)
                }}
            />
        </>
    )
}

export default GenericDocumentStepper
